import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import DishesAPI from '../api/DishesAPI'
import { QUERY, spiceLevelsModel } from '../api/DishesAPI.model'

/**
 * The spice level dictionary, and the translation between the two forms it
 * takes: the rating field speaks a number, the dish payload speaks an id.
 *
 * Matched on the backend's own `level` column, never on the list's position —
 * `SpiceLevelsRepository.getAll` has no ORDER BY, so the order it returns is
 * whatever Postgres chooses that day.
 *
 * Both helpers read the cache when they are called rather than closing over the
 * query result. The dish modal freezes its `onSubmit` inside an effect whose
 * dependencies do not include this data, so a helper that captured the array
 * would keep mapping against an empty list for as long as that modal stayed
 * open. Called this way they cannot go stale, and their identity is constant.
 */
export function useSpiceLevelsQuery() {
  const queryClient = useQueryClient()

  const { data: spiceLevels, isLoading: spiceLevelsLoading } = useQuery({
    queryKey: [QUERY.SPICE_LEVELS],
    queryFn: DishesAPI.getAllSpiceLevels
  })

  const readLevels = useCallback(
    () =>
      queryClient.getQueryData<spiceLevelsModel[]>([QUERY.SPICE_LEVELS]) ?? [],
    [queryClient]
  )

  /** Rating value (1-3) to the id the dish payload carries. */
  const spiceLevelIdByLevel = useCallback(
    (level?: number | null): string | null =>
      typeof level === 'number'
        ? (readLevels().find(item => item.level === level)?.id ?? null)
        : null,
    [readLevels]
  )

  /** The saved id back to the number the rating renders. */
  const levelBySpiceLevelId = useCallback(
    (id?: string | null): number | null =>
      id ? (readLevels().find(item => item.id === id)?.level ?? null) : null,
    [readLevels]
  )

  return {
    spiceLevels,
    spiceLevelsLoading,
    spiceLevelIdByLevel,
    levelBySpiceLevelId
  }
}
